import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { ReplaceStock } from '../../api/dto';
import { replaceStockSchema } from '../../utils/zodSchemas';
import { useReplaceStock } from '../../api/queries';
import { fr } from '../../i18n/fr';
import { STOCK_STATE_LABEL } from '../../utils/mapping';
import ProductSelectorField from './fields/ProductSelectorField';
import { ProductSelectionItem } from '../../types';

interface Props {
  initialProductId?: string;
  initialProduct?: ProductSelectionItem | null;
  onSubmitted?: () => void;
}

const ReplaceForm: React.FC<Props> = ({ initialProductId, initialProduct, onSubmitted }) => {
  const mutation = useReplaceStock();
  const defaultValues = useMemo(
    () => ({
      store_id: '',
      from_product_id: initialProductId ?? '',
      to_product_id: '',
      stock_state: 'available' as const,
      quantity: 1,
    }),
    [initialProductId]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<ReplaceStock>({
    resolver: zodResolver(replaceStockSchema),
    defaultValues,
  });

  useEffect(() => {
    setValue('from_product_id', initialProductId ?? '');
  }, [initialProductId, setValue]);

  const onSubmit = (values: ReplaceStock) => {
    const payload: ReplaceStock = {
      ...values,
      store_id: values.store_id.trim(),
      from_product_id: values.from_product_id.trim(),
      to_product_id: values.to_product_id.trim(),
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        reset({ ...defaultValues, stock_state: values.stock_state });
        onSubmitted?.();
      },
    });
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      aria-label="replace-form"
    >
      {mutation.isError && (
        <Alert severity="error">{(mutation.error as Error).message}</Alert>
      )}
      <TextField
        label="Magasin"
        {...register('store_id')}
        error={!!errors.store_id}
        helperText={errors.store_id?.message}
        fullWidth
      />
      <ProductSelectorField
        control={control}
        name="from_product_id"
        label="Produit origine"
        error={!!errors.from_product_id}
        helperText={errors.from_product_id?.message}
        initialSelection={initialProduct ?? null}
      />
      <ProductSelectorField
        control={control}
        name="to_product_id"
        label="Produit remplacement"
        error={!!errors.to_product_id}
        helperText={errors.to_product_id?.message}
      />
      <TextField select label={fr.stockState} {...register('stock_state')} fullWidth>
        {Object.entries(STOCK_STATE_LABEL).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="number"
        label={fr.quantity}
        inputProps={{ min: 0, step: 1 }}
        {...register('quantity', { valueAsNumber: true })}
        error={!!errors.quantity}
        helperText={errors.quantity?.message}
        fullWidth
      />
      <Button type="submit" variant="contained" disabled={mutation.isPending}>
        {fr.saveMovement}
      </Button>
    </Stack>
  );
};

export default ReplaceForm;
